import React from "react";
import Head from "next/head";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Shield,
  Heart,
  Zap,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import RelatedGuides from "@/components/seo/RelatedGuides";
import { useAuth } from "@/lib/supabase/AuthContext";

export default function NoQuestionIsAStupidQuestion() {
  const { user } = useAuth();
  const dashboardHref = user ? "/dashboard" : "/signup?next=/dashboard";

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <Head>
        <title>
          No Question Is a Stupid Question | Learning Without Fear | Durmah
        </title>
        <meta
          name="description"
          content="Why students hesitate to ask questions at university, and how private, judgement-free learning helps them understand more, stress less, and grow in confidence."
        />
        <meta
          name="keywords"
          content="asking questions without fear, learning anxiety, students afraid to ask questions, judgement free learning, university learning psychology, Durmah, Durham Law"
        />
        <link key="route-canonical"
          rel="canonical"
          href="https://www.durmah.ai/articles/no-question-is-a-stupid-question"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="No Question Is a Stupid Question | Learning Without Fear"
        />
        <meta
          property="og:description"
          content="Why students hesitate to ask questions at university, and how private, judgement-free learning helps them understand more, stress less, and grow in confidence."
        />
        <meta key="route-og-url"
          property="og:url"
          content="https://www.durmah.ai/articles/no-question-is-a-stupid-question"
        />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Durmah" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="No Question Is a Stupid Question | Learning Without Fear"
        />
        <meta
          name="twitter:description"
          content="Why students hesitate to ask questions at university, and how private, judgement-free learning helps them understand more, stress less, and grow in confidence."
        />
      </Head>

      <main className="flex-1">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <Link
            href="/resources"
            prefetch={false}
            className="inline-flex items-center gap-2 text-sm text-indigo-600 font-medium mb-8 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Guides Hub
          </Link>

          <div className="prose prose-indigo max-w-none">
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 font-serif leading-tight">
              No Question Is a Stupid Question: Why Fear Stops Students from
              Learning
            </h1>

            <h2 className="text-xl md:text-2xl font-bold text-gray-700 mb-4 mt-0">
              Why Students Don’t Ask Questions: How That Hurts Learning
            </h2>

            <p className="text-lg text-gray-600 leading-relaxed mb-8 italic border-l-4 border-indigo-100 pl-4 py-2">
              How private, judgement-free questioning helps students understand
              more, stress less, and grow in confidence.
            </p>

            <p>
              University is often described as a time of intellectual growth,
              independence, and discovery. But there is a quieter reality many
              students experience, one that rarely gets discussed openly.
            </p>

            <p>
              It isn’t a lack of ability, and it isn’t laziness. Often it isn’t
              even academic difficulty.
            </p>

            <p className="font-bold text-gray-900 text-xl">It’s fear.</p>

            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
              The unspoken reality of university life
            </h3>

            <p>
              Lecture theatres feel intimidating, seminars move quickly, peers
              sound confident, and tutors always seem busy.
            </p>

            <p>Many students carry questions they want to ask, but don’t.</p>

            <p>Not because they don’t care, but because they’re afraid of:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>sounding “stupid”</li>
              <li>interrupting the flow of a lecture</li>
              <li>being judged by peers</li>
              <li>exposing gaps in understanding</li>
              <li>struggling with accents, phrasing, or confidence</li>
            </ul>

            <p>So the question stays unasked.</p>

            <p>And something subtle but serious happens next.</p>

            <p>
              Confusion compounds, confidence drops, and anxiety builds.
              Students fall behind, quietly and often without anyone noticing.
            </p>

            <p>
              This is not really an academic failure. It’s a psychological
              access problem: a barrier to asking, not a lack of ability.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
              Why fear blocks learning more than difficulty
            </h3>

            <p>Understanding rarely breaks at big, obvious moments.</p>

            <p>It breaks at:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>one unclear sentence</li>
              <li>one unfamiliar concept</li>
              <li>one assumption everyone else seems to understand</li>
            </ul>

            <p>Left unaddressed, small gaps grow into major obstacles.</p>

            <p>
              But fear stops students from seeking clarification at the exact
              moment it’s needed most.
            </p>

            <p>Over time, this leads to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>hesitation in seminars</li>
              <li>weaker written arguments</li>
              <li>avoidance rather than engagement</li>
              <li>increased stress and self-doubt</li>
            </ul>

            <p>
              The issue isn’t intelligence. It’s the emotional cost of asking
              for help.
            </p>

            {/* Contextual Callout */}
            <div className="not-prose my-12 p-8 bg-indigo-50 rounded-3xl border border-indigo-100 shadow-sm relative overflow-hidden">
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Ready to ask better questions?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Once fear is removed, the next step is learning how to ask
                    questions that get you unstuck faster: in seminars,
                    tutorials, and essays.
                  </p>
                  <Link
                    href="/learn/how-to-ask-better-legal-questions"
                    prefetch={false}
                  >
                    <button className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100">
                      Read: How to Ask Better Legal Questions{" "}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
                <div className="hidden md:block p-4 bg-white rounded-2xl shadow-sm border border-indigo-50">
                  <Zap className="w-12 h-12 text-indigo-500" />
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
              How Durmah changes the equation
            </h3>

            <p>Durmah was designed with this invisible barrier in mind.</p>

            <p>It quietly removes fear from the learning process.</p>

            <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">
              A judgement-free space
            </h4>

            <p>
              Inside Durmah, there is no such thing as an embarrassing
              question.
            </p>

            <p>The assistant does not:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>rush the student</li>
              <li>dismiss “basic” queries</li>
              <li>compare them to others</li>
              <li>show impatience or judgement</li>
            </ul>

            <p>Every question is treated as valid.</p>

            <h4 className="text-xl font-bold text-gray-900 mt-8 mb-4">
              Private clarification, exactly when confusion arises
            </h4>

            <p>Students don’t need to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>wait for office hours</li>
              <li>draft careful emails</li>
              <li>raise a hand in a crowded lecture hall</li>
              <li>ask friends and risk embarrassment</li>
            </ul>

            <p>They can ask:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>immediately</li>
              <li>privately</li>
              <li>repeatedly</li>
              <li>in their own words</li>
            </ul>

            <p>This is learning at the speed of courage, not confidence.</p>

            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
              Who benefits most
            </h3>

            <p>The students who benefit most are often:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>shy or quiet students</li>
              <li>international students</li>
              <li>first-generation university students</li>
              <li>conscientious high-potential learners</li>
              <li>students who almost understand but hesitate to ask</li>
            </ul>

            <p>
              These students are not weak. They are capable, but cautious.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
              What this means for confidence and performance
            </h3>

            <p>When students are free to ask questions without fear:</p>
            <ul className="list-disc pl-6 space-y-2 mb-8">
              <li>understanding becomes clearer</li>
              <li>participation improves naturally</li>
              <li>written work strengthens</li>
              <li>anxiety reduces</li>
              <li>healthier learning habits form</li>
            </ul>

            <p>
              Tools like Year at a Glance, assignment support, and{" "}
              <Link
                href="/articles/quiz-me"
                prefetch={false}
                className="text-indigo-600 hover:underline"
              >
                practice quizzes
              </Link>{" "}
              help make sure that once a gap is filled, the knowledge is
              reinforced and applied within the student's actual curriculum.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
              A message to parents
            </h3>

            <p>Not all students ask for help out loud.</p>

            <p>Some need a quiet, private space to think, ask, and grow.</p>

            <p>
              Durmah supports academic progress and emotional wellbeing during
              one of the most demanding transitions in a young person’s life.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
              Learning without fear
            </h3>

            <p>
              Education works best when curiosity is not punished by
              embarrassment.
            </p>

            <p>
              By removing fear from the act of asking questions, Durmah gives
              students confidence, clarity, and the freedom to learn properly.
            </p>

            <p>No student should have to struggle in silence.</p>
          </div>

          <RelatedGuides
            currentSlug="no-question-is-a-stupid-question"
            categories={["Psychology"]}
            pinnedSlugs={["how-to-ask-better-legal-questions"]}
          />

          {/* CTA Block */}
          <div className="mt-20 p-8 md:p-12 bg-gradient-to-br from-indigo-800 to-indigo-950 rounded-3xl text-center text-white shadow-2xl">
            <div className="flex justify-center mb-6">
              <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-sm border border-white/20">
                <Heart className="w-8 h-8 text-pink-400 fill-pink-400" />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Explore Durmah
            </h2>
            <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
              Study with judgement-free AI assistance and full-year visibility.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={dashboardHref} prefetch={false}>
                <Button className="bg-white text-indigo-900 hover:bg-gray-100 px-8 py-4 text-lg w-full sm:w-auto font-bold rounded-2xl shadow-lg">
                  Open Dashboard
                </Button>
              </Link>
              <Link href="/signup?next=/dashboard" prefetch={false}>
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg w-full sm:w-auto font-bold rounded-2xl backdrop-blur-sm"
                >
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}

